import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import { Search, Plus, User, Upload, Download, X } from 'lucide-react';
import { getTagColor, formatPhoneNumber } from '../utils/helpers';
import PatientProfileModal from '../components/PatientProfileModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const PatientsPage = ({ user, onLogout }) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch patients with server-side search
  const fetchPatients = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) {
        params.append('search', search);
      }
      const response = await api.get(`/patients?${params.toString()}`);
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Debounced search - triggers server search after 300ms of no typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchPatients(searchQuery);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchPatients]);

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/patients/template/download', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'patient_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/patients/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResult(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} patients uploaded successfully!`);
        fetchPatients(); // Refresh the list
      }
      
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} patients failed to upload`);
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload patients');
      setUploadResult({
        success_count: 0,
        error_count: 1,
        errors: [error.response?.data?.detail || 'Upload failed']
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Since search is now server-side, we use patients directly
  const displayedPatients = patients;

  const isAdmin = user?.role === 'Admin';
  const needsMoreChars = !isAdmin && searchQuery.trim().length < 3;

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const res = await api.get(`/patients/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `patients_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Patient export downloaded');
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Only Admin can export patients' : 'Export failed');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Patients</h1>
          <p className="text-gray-600" data-testid="patients-count-text">
            {loading ? 'Loading...' : needsMoreChars ? 'Type at least 3 characters to search patients' : `${patients.length} patients found`}
          </p>
        </div>

        <div className="mb-6 flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="search-patients-input"
              placeholder="Search by name, phone or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-xl"
            />
          </div>
          {isAdmin && (
            <>
              <Button
                onClick={handleExportCsv}
                variant="outline"
                className="h-12 px-6 border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="patients-export-csv-btn"
              >
                <Download className="w-5 h-5 mr-2" />
                Export CSV
              </Button>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white h-12 px-6"
                data-testid="patients-bulk-upload-btn"
              >
                <Upload className="w-5 h-5 mr-2" />
                Bulk Upload
              </Button>
            </>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : displayedPatients.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200" data-testid="patients-empty-state">
            <p className="text-gray-500">
              {needsMoreChars
                ? 'Search by name, phone or email (minimum 3 characters) to find a patient'
                : searchQuery ? 'No patients found matching your search' : 'No patients found'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayedPatients.map(patient => (
              <div
                key={patient.id}
                data-testid={`patient-card-${patient.id}`}
                onClick={() => setSelectedPatient(patient.id)}
                className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:shadow-lg transition cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{patient.name}</h3>
                      <p className="text-sm text-gray-600">
                        {patient.country_code || '+91'} {formatPhoneNumber(patient.primary_phone)}
                      </p>
                    </div>
                  </div>
                </div>
                {patient.tags && patient.tags.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {patient.tags.map(tag => (
                      <Badge key={tag} className={`${getTagColor(tag)} text-xs`}>
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                {patient.average_lateness > 0 && (
                  <div className="mt-3 text-xs text-amber-600">
                    Avg Lateness: {Math.round(patient.average_lateness)} min
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedPatient && (
        <PatientProfileModal
          isOpen={!!selectedPatient}
          onClose={() => setSelectedPatient(null)}
          patientId={selectedPatient}
          user={user}
        />
      )}

      {/* Bulk Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Upload Patients</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Download the CSV template using the button below</li>
                <li>Fill in your patient data following the template format</li>
                <li>Upload the completed CSV file</li>
              </ol>
              <div className="mt-3">
                <p className="text-xs text-blue-700 font-medium">CSV Format:</p>
                <ul className="text-xs text-blue-700 space-y-1 mt-1 ml-4">
                  <li>• <strong>name</strong>: Patient full name (required)</li>
                  <li>• <strong>primary_phone</strong>: Main phone number (required, must be unique)</li>
                  <li>• <strong>country_code</strong>: Country code without + sign, e.g., 91 for India (optional, default 91)</li>
                  <li>• <strong>additional_phone_numbers</strong>: Comma-separated phone numbers (optional)</li>
                  <li>• <strong>tags</strong>: Comma-separated tags like VIP,Regular (optional)</li>
                  <li>• <strong>average_lateness_minutes</strong>: Number (optional, default 0)</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={downloadTemplate}
                variant="outline"
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
              />
              <label
                htmlFor="csv-upload"
                className="cursor-pointer block"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  Click to upload CSV file
                </p>
                <p className="text-xs text-gray-500">
                  Only .csv files are supported
                </p>
              </label>
            </div>

            {uploading && (
              <div className="text-center text-gray-600">
                <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                Uploading patients...
              </div>
            )}

            {uploadResult && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Upload Results:</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-green-600 font-medium">✅ Successful:</span>
                    <span className="font-bold">{uploadResult.success_count}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600 font-medium">❌ Failed:</span>
                    <span className="font-bold">{uploadResult.error_count}</span>
                  </div>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Errors:</p>
                      <div className="bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto">
                        {uploadResult.errors.map((error, index) => (
                          <p key={index} className="text-xs text-red-700">{error}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadResult(null);
                }}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PatientsPage;
